import pickBy from 'lodash/pickBy'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoRectangularManway,
  GeckoRectangularManwayJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  compassOrientationFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoFixedSensor,
  GeckoFixedSensorJSON,
} from 'types/assetModel/features/geckoFixedSensor'
import {
  GeckoManway,
  GeckoManwayJSON,
} from 'types/assetModel/features/geckoManway'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { parseNumber } from 'utils/parsingHelpers'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

const { pi } = forecastMath

export type HorizontalTankShellAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.HorizontalTankShell
  length: number
  radius: number
  features: Record<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPlateJSON
    | GeckoNozzleJSON
    | GeckoManwayJSON
    | GeckoFixedSensorJSON
    | GeckoRectangularManwayJSON
  >
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  nominal: {
    wall_thickness: number | undefined
  }
  tmin: number | undefined
  compass_orientation: number | undefined
}

export const HorizontalTankShellAvailableFeatures: GeckoFeatureModelType[] = [
  // these also have heads but right now we have not implemented these according to docs
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.HorizontalWeld,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.Nozzle,
  GeckoFeatureModelType.Manway,
  GeckoFeatureModelType.RectangularManway,
  GeckoFeatureModelType.FixedSensor,
]

export const NewHorizontalTankShellAssetModelJSON = {
  name: 'New Tank Shell',
  type: GeckoComponentModelType.HorizontalTankShell,
  length: 0,
  radius: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  nominal: {
    wall_thickness: undefined,
  },
  compass_orientation: undefined,
} as HorizontalTankShellAssetModelJSON

export class GeckoHorizontalTankShell extends GeckoEditableComponent {
  type: GeckoComponentModelType.HorizontalTankShell
  radius: GeckoMeasurement
  length: GeckoMeasurement
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  nozzles: Map<string, GeckoNozzle>
  manways: Map<string, GeckoManway>
  rectangularManways: Map<string, GeckoRectangularManway>
  fixedSensors: Map<string, GeckoFixedSensor>
  plates: Map<string, GeckoPlate>
  verticalWelds: Map<string, GeckoVerticalWeld>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  wallNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    length: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    nozzles: Map<string, GeckoNozzle>,
    manways: Map<string, GeckoManway>,
    rectangularManways: Map<string, GeckoRectangularManway>,
    fixedSensors: Map<string, GeckoFixedSensor>,
    plates: Map<string, GeckoPlate>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    wallNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)

    this.type = GeckoComponentModelType.HorizontalTankShell
    this.radius = radius
    this.length = length
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.nozzles = nozzles
    this.manways = manways
    this.rectangularManways = rectangularManways
    this.fixedSensors = fixedSensors
    this.plates = plates
    this.verticalWelds = verticalWelds
    this.horizontalWelds = horizontalWelds
    this.wallNominalThickness = wallNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get manwaysArray() {
    return Array.from(this.manways.values())
  }

  public get rectangularManwaysArray() {
    return Array.from(this.rectangularManways.values())
  }

  public get fixedSensorsArray() {
    return Array.from(this.fixedSensors.values())
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public static CreateFromJSON(
    id: string,
    json: HorizontalTankShellAssetModelJSON
  ) {
    const name = json.name as string
    const shellRadius = GeckoMeasurement.CreateFromValue(
      json.radius as number,
      GeckoMeasurementUnit.Meters
    )
    const shellLength = GeckoMeasurement.CreateFromValue(
      json.length as number,
      GeckoMeasurementUnit.Meters
    )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData = json.features as Record<
      string,
      | GeckoPlateJSON
      | GeckoHorizontalWeldJSON
      | GeckoVerticalWeldJSON
      | GeckoManwayJSON
      | GeckoRectangularManwayJSON
      | GeckoNozzleJSON
      | GeckoFixedSensorJSON
    >
    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    })
    const plates = GeckoPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoPlateJSON>
    )

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })

    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Manway
    })
    const manways = GeckoManway.CreateMapFromJSON(
      id,
      childManwayJSON as Record<string, GeckoManwayJSON>
    )

    const childRectangularManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.RectangularManway
    })
    const rectangularManways = GeckoRectangularManway.CreateMapFromJSON(
      id,
      childRectangularManwayJSON as Record<string, GeckoRectangularManwayJSON>
    )

    const childFixedSensorJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.FixedSensor
    })
    const fixedSensors = GeckoFixedSensor.CreateMapFromJSON(
      id,
      childFixedSensorJSON as Record<string, GeckoFixedSensorJSON>
    )

    const nominalJSON = json.nominal as Record<string, number>
    const parsedWallNominalThickness = parseNumber(nominalJSON.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const parsedTmin = parseNumber(json.tmin)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const compassOrientation = parseNumber(json.compass_orientation)

    return new GeckoHorizontalTankShell(
      id,
      json,
      name,
      shellRadius,
      shellLength,
      componentBounds,
      sceneConfig,
      nozzles,
      manways,
      rectangularManways,
      fixedSensors,
      plates,
      verticalWelds,
      horizontalWelds,
      wallNominalThickness,
      tmin,
      compassOrientation
    )
  }

  public asJSON(): HorizontalTankShellAssetModelJSON {
    return generateTankShell(
      this.name,
      this.length,
      this.radius,
      this.wallNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.json as HorizontalTankShellAssetModelJSON
    )
  }
}

const generateTankShell = (
  name: string,
  length: GeckoMeasurement,
  radius: GeckoMeasurement,
  wallNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherTankShellData: HorizontalTankShellAssetModelJSON = NewHorizontalTankShellAssetModelJSON
): HorizontalTankShellAssetModelJSON => {
  const tankLength = length
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tankRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tankCircumference = Number(
    (2 * pi * tankRadius).toPrecision(assetModelPrecision)
  )

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.HorizontalTankShell,
    length: tankLength,
    radius: tankRadius,
    features: otherTankShellData.features,
    scene_config: otherTankShellData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: tankLength,
      y_end: tankCircumference,
    },
    nominal: {
      wall_thickness: wallNominalThicknessMeters,
    },
    tmin: tminMeters,
    compass_orientation: compassOrientation,
  }
}

export const HorizontalTankShellAssetModelFormFields = (
  tankShellJSON: HorizontalTankShellAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Shell Name',
      value: tankShellJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'length',
      label: 'Length',
      value: tankShellJSON.length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    // Diameter is the industry standard measurement for tanks, so convert to radius and back
    {
      dataKey: 'radius',
      label: 'Diameter',
      value: tankShellJSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    wallThicknessFormField(tankShellJSON.nominal.wall_thickness),
    tminFormField(tankShellJSON.tmin),
    compassOrientationFormField(tankShellJSON.compass_orientation),
  ]
}

export const HorizontalTankShellJSONFromFormFields = (
  component: HorizontalTankShellAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  return generateTankShell(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'length')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2, //radius is diameter
      GeckoMeasurementUnit.Meters
    ),
    parsedWallNominalThickness !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedWallNominalThickness,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedTmin !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTmin,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    component
  )
}
